import { Pipe, PipeTransform } from '@angular/core';
import { LatLng, Point } from 'leaflet';
import proj4 from 'proj4';
import { CRSCode, MGA_54_55_THRESHOLD, VicGridCRS } from '../projections';

@Pipe({
  name: 'decimalDegrees',
  standalone: true,
})
export class DecimalDegreesPipe implements PipeTransform {
  transform(latlng: LatLng): string {
    return `${latlng.lng.toFixed(2)}, ${latlng.lat.toFixed(2)}`;
  }
}

@Pipe({
  name: 'geographicCoords',
  standalone: true,
})
export class GeographicCoordsPipe implements PipeTransform {
  toDegreesMinutesAndSeconds(coordinate: number) {
    const absolute = Math.abs(coordinate);
    let degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    let minutes = Math.floor(minutesNotTruncated);
    let seconds = Math.round((minutesNotTruncated - minutes) * 600) / 10;

    if (seconds >= 60) {
      seconds = 0;
      minutes += 1;
    }

    if (minutes >= 60) {
      minutes = 0;
      degrees += 1;
    }

    return {
      degrees,
      minutes,
      seconds,
    };
  }

  transform(latlng: LatLng): string {
    const lat = this.toDegreesMinutesAndSeconds(latlng.lat);
    const lng = this.toDegreesMinutesAndSeconds(latlng.lng);

    const latDir = latlng.lat >= 0 ? 'N' : 'S';
    const lngDir = latlng.lng >= 0 ? 'E' : 'W';
    return `${lat.degrees}°${this.padZero(lat.minutes)}'${this.formatSeconds(lat.seconds)}" ${latDir}, ${lng.degrees}°${this.padZero(lng.minutes)}'${this.formatSeconds(lng.seconds)}" ${lngDir}`;
  }

  private padZero(num: number | string, size = 2): string {
    return num.toString().padStart(size, '0');
  }

  private formatSeconds(seconds: number): string {
    return seconds.toFixed(1).padStart(4, '0');
  }
}

@Pipe({
  name: 'vicGrid',
  standalone: true,
})
export class VicGridPipe implements PipeTransform {
  transform(latlng: LatLng) {
    return VicGridCRS.project(latlng);
  }
}

export interface MGAPoint {
  point: Point;
  name: '54' | '55';
}

@Pipe({
  name: 'mga',
  standalone: true,
})
export class MGAPipe implements PipeTransform {
  transform(latLng: LatLng): MGAPoint {
    if (latLng.lng > MGA_54_55_THRESHOLD) {
      const [x, y] = proj4(CRSCode.GDA2020, CRSCode.MGA55, [
        latLng.lng,
        latLng.lat,
      ]);
      return {
        point: new Point(x, y),
        name: '55',
      };
    } else {
      const [x, y] = proj4(CRSCode.GDA2020, CRSCode.MGA54, [
        latLng.lng,
        latLng.lat,
      ]);
      return {
        point: new Point(x, y),
        name: '54',
      };
    }
  }
}

@Pipe({
  name: 'latlng',
  standalone: true,
})
export class LatLngPipe implements PipeTransform {
  transform(point: { x: number; y: number }) {
    return VicGridCRS.unproject(new Point(point.x, point.y));
  }
}

@Pipe({
  name: 'point',
  standalone: true,
})
export class PointPipe implements PipeTransform {
  transform(point: Point, decimalPlaces?: number): string {
    const precision =
      typeof decimalPlaces === 'number' && decimalPlaces > 0
        ? Math.trunc(decimalPlaces)
        : undefined;
    return `${point.x.toFixed(precision)}, ${point.y.toFixed(precision)}`;
  }
}

/**
 * Convert coodinates to Degrees Minutes Seconds (DMS) format.
 * e.g. 37.8136, 144.9631 => dd mm ss.ss
 */
@Pipe({
  name: 'dms',
  standalone: true,
})
export class DMSPipe implements PipeTransform {
  transform(value?: string | number | null): string {
    if (!value && value !== 0) {
      return '';
    }

    // return the value as is if it is already in DMS format
    if (
      typeof value === 'string' &&
      /^-?\d{1,3}\s\d{1,2}\s\d{1,2}\.\d{1,2}$/.test(value)
    ) {
      return value;
    }

    const coordinate = typeof value === 'string' ? parseFloat(value) : value;
    const absolute = Math.abs(coordinate);
    const degrees = Math.floor(absolute);
    const minutesDecimal = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = (minutesDecimal - minutes) * 60;

    const paddedDegrees = degrees.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSeconds = seconds.toFixed(2).padStart(5, '0');
    return `${paddedDegrees} ${paddedMinutes} ${paddedSeconds}`;
  }
}
